import { inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { AuthService } from '../services/auth/auth.service';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { throwError, Observable, EMPTY } from 'rxjs';
import { catchError, switchMap, shareReplay, finalize } from 'rxjs/operators';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

let refreshToken$: Observable<string> | null = null;

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const authToken = authService.getToken();

  if (!req.url.startsWith(environment.apiUrl)) {
    return next(req);
  }

  if (!authToken) {
    router.navigate(['/login']);
    return throwError(() => new Error('Token not found'));
  }

  const clonedReq = req.clone({
    setHeaders: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  return next(clonedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && req.url.startsWith(environment.apiUrl)) {
        return handle401Error(req, next, authService, router);
      }
      return throwError(() => error);
    })
  );
};

function handle401Error(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router
): Observable<any> {
  if (refreshToken$) {
    return refreshToken$.pipe(
      switchMap((token) => {
        return next(addTokenHeader(req, token));
      }),
      catchError(() => {
        authService.clearToken();
        router.navigate(['/login']);
        return EMPTY;
      })
    );
  }

  refreshToken$ = authService.refreshToken().pipe(
    shareReplay({ refCount: true, bufferSize: 1 }),
    catchError((err) => {
      refreshToken$ = null;

      // If refresh failed, clear the token and redirect to login
      if (err instanceof InteractionRequiredAuthError) {
        authService.clearToken();
        router.navigate(['/login']);
      } else {
        authService.clearToken();
        router.navigate(['/login']);
      }
      throw err;
    }),
    finalize(() => {
      refreshToken$ = null;
    })
  );

  // Use the shared token to retry the request
  return refreshToken$.pipe(
    switchMap((token: string) => {
      return next(addTokenHeader(req, token));
    }),
    catchError(() => {
      authService.clearToken();
      router.navigate(['/login']);
      return EMPTY;
    })
  );
}

function addTokenHeader(
  req: HttpRequest<unknown>,
  token: string
): HttpRequest<unknown> {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}
